import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Copy, Eye, EyeOff, Bot, Terminal, KeyRound, ListChecks,
  MessageSquareQuote, Server, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const TOOLS = [
  ['get_overview', 'Выручка, заказы, средний чек, клиенты, отмены (+ % к прошлому периоду)'],
  ['get_revenue_trend', 'Динамика выручки по дням / неделям / месяцам'],
  ['get_top_products', 'Топ товаров по выручке'],
  ['get_top_clients', 'Топ клиентов по выручке'],
  ['get_category_breakdown', 'Выручка по категориям'],
  ['get_payment_funnel', 'Воронка платежей Kaspi/ApiPay: конверсия, потери, зависшие'],
  ['get_churn', 'Клиенты в зоне оттока для возврата (win-back)'],
  ['get_container_aging', 'Старение тары — «забытые контейнеры» у клиентов'],
  ['get_delivery_ops', 'Операции доставки: маршруты, км, проблемы по водителям'],
];

const EXAMPLES = [
  'Покажи выручку и заказы за последние 30 дней',
  'Какая конверсия воронки платежей за неделю и сколько денег потеряно?',
  'Кто из клиентов в зоне высокого риска оттока? Отсортируй по выручке',
  'Топ-5 товаров за месяц',
  'Дай динамику выручки по неделям за 90 дней',
  'У кого зависла тара дольше 15 дней?',
];

function AdminMcpDocs({ user, onLogout }) {
  const { lang } = useParams();
  const currentLang = lang || 'ru';
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/analytics-viewers/mcp-info`, { headers: getAuthHeaders() })
      .then((r) => setInfo(r.data))
      .catch(() => toast.error('Не удалось загрузить данные MCP'));
  }, []);

  const key = info?.api_key || '';
  const base = info?.api_base || 'https://ainanan.kz';
  const masked = key ? key.slice(0, 6) + '•'.repeat(Math.max(0, key.length - 10)) + key.slice(-4) : '';

  const copy = (txt, label) => { navigator.clipboard.writeText(txt); toast.success(`${label} скопировано`); };

  const desktopConfig = `{
  "mcpServers": {
    "ainanan-analytics": {
      "command": "/ABSOLUTE/PATH/mcp_server/.venv/bin/python",
      "args": ["/ABSOLUTE/PATH/mcp_server/server.py"],
      "env": {
        "AINANAN_API_BASE": "${base}",
        "ANALYTICS_MCP_API_KEY": "${key || 'PASTE_YOUR_KEY'}"
      }
    }
  }
}`;

  const setupCmds = `cd mcp_server
python3 -m venv .venv
./.venv/bin/pip install -r requirements.txt`;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-mcp-docs">
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/${currentLang}/admin`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Bot className="w-5 h-5 text-emerald-600" /> MCP для Claude — аналитика
            </h1>
            <p className="text-sm text-gray-500">Подключите Claude к аналитике сайта и спрашивайте данные текстом</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {/* Step 1: key */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Шаг 1. Ваш API-ключ (только чтение)</CardTitle>
            <CardDescription>Этот ключ даёт Claude доступ только к просмотру аналитики. Храните его в секрете.</CardDescription>
          </CardHeader>
          <CardContent>
            {info && !info.api_key_configured ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Ключ не задан в окружении (`ANALYTICS_MCP_API_KEY`). Обратитесь к разработчику, чтобы его добавить.
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                <code className="text-sm flex-1 break-all" data-testid="mcp-key">{showKey ? key : masked}</code>
                <Button size="sm" variant="ghost" onClick={() => setShowKey((v) => !v)} data-testid="toggle-key">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => copy(key, 'Ключ')} data-testid="copy-key">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">Базовый URL данных: <code>{base}</code></p>
          </CardContent>
        </Card>

        {/* Step 2: install */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Terminal className="w-4 h-4" /> Шаг 2. Установка MCP-сервера</CardTitle>
            <CardDescription>На компьютере, где запущен Claude Desktop. Папка <code>mcp_server</code> — в коде проекта.</CardDescription>
          </CardHeader>
          <CardContent>
            <CodeBlock text={setupCmds} onCopy={() => copy(setupCmds, 'Команды')} testid="setup-cmds" />
          </CardContent>
        </Card>

        {/* Step 3: claude desktop config */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4" /> Шаг 3. Конфиг Claude Desktop</CardTitle>
            <CardDescription>
              Вставьте в <code>claude_desktop_config.json</code> (macOS: <code>~/Library/Application Support/Claude/</code>,
              Windows: <code>%APPDATA%\Claude\</code>). Замените <b>/ABSOLUTE/PATH</b> на реальный путь к папке.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CodeBlock text={desktopConfig} onCopy={() => copy(desktopConfig, 'Конфиг')} testid="desktop-config" />
            <p className="text-xs text-gray-500 mt-2">Перезапустите Claude Desktop → появятся инструменты «ainanan-analytics».</p>
          </CardContent>
        </Card>

        {/* Tools */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><ListChecks className="w-4 h-4" /> Что умеет (9 инструментов)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-gray-100">
              {TOOLS.map(([name, desc]) => (
                <div key={name} className="py-2 flex items-start gap-3">
                  <Badge variant="outline" className="font-mono text-xs shrink-0">{name}</Badge>
                  <span className="text-sm text-gray-600">{desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Examples */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><MessageSquareQuote className="w-4 h-4" /> Примеры вопросов к Claude</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-2">
              {EXAMPLES.map((q, i) => (
                <div key={i} className="text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-emerald-900">«{q}»</div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Важно</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-1.5">
            <p>• Доступ <b>только на чтение</b> — изменить данные через MCP нельзя.</p>
            <p>• Для командного доступа по URL есть режим <code>streamable-http</code> (см. README в папке <code>mcp_server</code>) — нужен платный тариф Claude.</p>
            <p>• Ротация ключа: поменяйте <code>ANALYTICS_MCP_API_KEY</code> в окружении бэкенда и обновите конфиг.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CodeBlock({ text, onCopy, testid }) {
  return (
    <div className="relative">
      <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto" data-testid={testid}>{text}</pre>
      <Button size="sm" variant="secondary" className="absolute top-2 right-2 h-7" onClick={onCopy}>
        <Copy className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export default AdminMcpDocs;
